import { useEffect, useState } from 'react';

/** Consecutive presented frames to see before the rate is trusted. */
const MIN_SAMPLES = 8;
/** Enough to settle a median; measuring stops here. */
const MAX_SAMPLES = 24;
const MIN_FPS = 1;
const MAX_FPS = 240;
/** A change smaller than this is measurement noise, not a new rate. */
const FPS_EPSILON = 0.05;

interface FrameMetadata {
  mediaTime: number;
  presentedFrames: number;
}

type FrameCallback = (now: number, metadata: FrameMetadata) => void;

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: FrameCallback) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * The rate from gaps between consecutive presented frames, in seconds. The
 * median shrugs off the odd stutter; a rate outside any real video's is no rate.
 */
export function frameRateFromGaps(gapsSeconds: number[]): number | undefined {
  if (gapsSeconds.length < MIN_SAMPLES) return undefined;
  const sorted = [...gapsSeconds].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
  const fps = 1 / median;
  if (!Number.isFinite(fps) || fps < MIN_FPS || fps > MAX_FPS) return undefined;
  return fps;
}

/**
 * The video's real frame rate, measured while it plays from the media time of
 * frames the browser presents back to back — `requestVideoFrameCallback`
 * reports no rate itself. Undefined until measured, and for browsers without
 * the API, so callers fall back to a default.
 */
export function useVideoFrameRate(element: HTMLVideoElement | null, enabled: boolean): number | undefined {
  const [measured, setMeasured] = useState<{ element: HTMLVideoElement; fps: number } | null>(null);

  useEffect(() => {
    const video = element as VideoWithFrameCallback | null;
    if (!enabled || !video || !video.requestVideoFrameCallback) return undefined;
    const requestFrame = video.requestVideoFrameCallback.bind(video);
    let gaps: number[] = [];
    let previous: FrameMetadata | null = null;
    let handle = 0;
    let stopped = false;

    function onFrame(_now: number, metadata: FrameMetadata): void {
      if (stopped) return;
      const adjacent = previous !== null && metadata.presentedFrames - previous.presentedFrames === 1;
      // Only playback: a seek presents a frame at an arbitrary distance from the last.
      if (adjacent && previous && !video?.paused && metadata.mediaTime > previous.mediaTime) {
        gaps.push(metadata.mediaTime - previous.mediaTime);
      }
      previous = metadata;
      const fps = frameRateFromGaps(gaps);
      if (fps !== undefined) {
        setMeasured((current) => (current?.element === element && Math.abs(current.fps - fps) < FPS_EPSILON ? current : { element: video as HTMLVideoElement, fps }));
      }
      if (gaps.length >= MAX_SAMPLES) return;
      handle = requestFrame(onFrame);
    }

    // The same element is reused when its source changes (another cut, another entry): the old
    // rate no longer applies, and measuring — which stops once settled — starts over.
    function onNewSource(): void {
      video?.cancelVideoFrameCallback?.(handle);
      gaps = [];
      previous = null;
      setMeasured(null);
      handle = requestFrame(onFrame);
    }

    handle = requestFrame(onFrame);
    video.addEventListener('loadstart', onNewSource);
    video.addEventListener('emptied', onNewSource);
    return () => {
      stopped = true;
      video.cancelVideoFrameCallback?.(handle);
      video.removeEventListener('loadstart', onNewSource);
      video.removeEventListener('emptied', onNewSource);
    };
  }, [element, enabled]);

  return measured?.element === element ? measured?.fps : undefined;
}
